import {
  type BedSummaryDto,
  type DateRange,
  type PlanDto,
  intersectRanges,
  normalizeRanges,
} from '@room-mates/shared';

export interface BedOccupant {
  readonly guestId: string;
  readonly guestName: string;
  readonly sojournId: string;
  readonly sojournName: string;
  /** The nights, within the selected window, this guest actually occupies the bed. */
  readonly ranges: readonly DateRange[];
}

export interface UnassignedGuest {
  readonly guestId: string;
  readonly guestName: string;
  readonly sojournId: string;
  readonly sojournName: string;
  readonly message: string;
}

export interface NamedPlan {
  readonly sojournId: string;
  readonly sojournName: string;
  readonly plan: PlanDto;
}

/**
 * Who occupies each bed during `selection`, across every sojourn plan that
 * might reach into it.
 *
 * A plan assignment does not carry its own date range — it references a
 * segment index, and the segment's range is what has to be compared against
 * the window the user picked. Two assignments from different sojourns landing
 * on the same bed for overlapping nights is a real conflict nothing today
 * prevents at write time, so it is surfaced here rather than one silently
 * overwriting the other.
 */
export function occupantsByBed(
  plans: readonly NamedPlan[],
  selection: DateRange,
): Map<string, BedOccupant[]> {
  const byBedAndGuest = new Map<string, Map<string, BedOccupant & { ranges: DateRange[] }>>();

  for (const { sojournId, sojournName, plan } of plans) {
    for (const assignment of plan.assignments) {
      const segment = plan.segments[assignment.segmentIndex];
      if (segment === undefined) {
        continue;
      }
      const overlap = intersectRanges({ start: segment.start, end: segment.end }, selection);
      if (overlap === null) {
        continue;
      }

      const guestsInBed = byBedAndGuest.get(assignment.bedId) ?? new Map();
      byBedAndGuest.set(assignment.bedId, guestsInBed);

      const key = `${sojournId}:${assignment.guestId}`;
      const existing = guestsInBed.get(key);
      if (existing === undefined) {
        guestsInBed.set(key, {
          guestId: assignment.guestId,
          guestName: assignment.guestName,
          sojournId,
          sojournName,
          ranges: [overlap],
        });
      } else {
        existing.ranges.push(overlap);
      }
    }
  }

  const result = new Map<string, BedOccupant[]>();
  for (const [bedId, guestsInBed] of byBedAndGuest) {
    result.set(
      bedId,
      [...guestsInBed.values()].map((occupant) => ({
        ...occupant,
        ranges: normalizeRanges(occupant.ranges),
      })),
    );
  }
  return result;
}

export interface RoomGroup {
  readonly roomId: string;
  readonly roomName: string;
  readonly beds: readonly BedSummaryDto[];
}

export interface HouseGroup {
  readonly houseId: string;
  readonly houseName: string;
  readonly rooms: readonly RoomGroup[];
}

/** Beds already arrive sorted by house, then room, then position; grouping only has to preserve that order. */
export function groupByHouseAndRoom(beds: readonly BedSummaryDto[]): HouseGroup[] {
  const houses: HouseGroup[] = [];
  for (const bed of beds) {
    let house = houses.find((candidate) => candidate.houseId === bed.houseId);
    if (house === undefined) {
      house = { houseId: bed.houseId, houseName: bed.houseName, rooms: [] };
      houses.push(house);
    }
    let room = house.rooms.find((candidate) => candidate.roomId === bed.roomId);
    if (room === undefined) {
      room = { roomId: bed.roomId, roomName: bed.roomName, beds: [] };
      (house.rooms as RoomGroup[]).push(room);
    }
    (room.beds as BedSummaryDto[]).push(bed);
  }
  return houses;
}

/** Guests a plan could not seat at all, whose gap falls within the selected window. */
export function unassignedGuests(
  plans: readonly NamedPlan[],
  selection: DateRange,
): UnassignedGuest[] {
  const result: UnassignedGuest[] = [];
  for (const { sojournId, sojournName, plan } of plans) {
    for (const unassigned of plan.unassigned) {
      const segment = plan.segments[unassigned.segmentIndex];
      if (segment === undefined) {
        continue;
      }
      if (intersectRanges({ start: segment.start, end: segment.end }, selection) === null) {
        continue;
      }
      result.push({
        guestId: unassigned.guestId,
        guestName: unassigned.guestName,
        sojournId,
        sojournName,
        message: unassigned.message,
      });
    }
  }
  return result;
}
